/**
 * Status, filter, and display config for school years.
 */
import { CheckCircle2, Ban } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for a school-year status. */
export interface SchoolYearStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Field hints shown on the school-year create / edit form. */
export const SCHOOL_YEAR_FORM_HINTS = {
  code: "Code unique identifiant l'année scolaire (format YYYY-YYYY)",
  startDate: 'Ne doit pas chevaucher une autre année scolaire',
  endDate: 'Doit être après la date de début, sans chevauchement',
} as const;

/** React Query keys invalidated after a school-year create / save. */
export const SCHOOL_YEAR_FORM_QUERY_KEYS = {
  list: ['schoolYears'] as const,
  integrity: ['schoolYears', 'integrity'] as const,
  one: (id: string) => ['schoolYear', id] as const,
};

/** Badge config for each school-year status. */
export const SCHOOL_YEAR_STATUS_CONFIG: Record<string, SchoolYearStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  closed: {
    label: 'Clôturée',
    icon: Ban,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** School-year list filter state. */
export interface SchoolYearFilters {
  statuses: string[];
  isCurrent: boolean | null;
  periodPhases: ('ended' | 'upcoming')[];
  searchQuery?: string;
  startDateFrom?: Date;
  startDateTo?: Date;
  endDateFrom?: Date;
  endDateTo?: Date;
}

/** Filter group IDs for the school-years {@link DataTable} configure panel. */
export type FilterGroupId =
  | 'filter-status'
  | 'filter-current'
  | 'filter-search'
  | 'filter-date-range';

/** Default school-year list filters. */
export const DEFAULT_SCHOOL_YEAR_FILTERS: SchoolYearFilters = {
  statuses: [],
  isCurrent: null,
  periodPhases: [],
  searchQuery: '',
  startDateFrom: undefined,
  startDateTo: undefined,
  endDateFrom: undefined,
  endDateTo: undefined,
};

/** Configure-panel order for school-year filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-status',
  'filter-current',
  'filter-search',
  'filter-date-range',
];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-status': 'Statut',
  'filter-current': 'Type',
  'filter-search': 'Recherche',
  'filter-date-range': 'Période',
};

/** Returns the badge color name for a school year status. */
export function getSchoolYearStatusColor(status: string): 'success' | 'error' {
  return status === 'active' ? 'success' : 'error';
}
